import { isEmpty } from "lodash-es"
import { Composer } from "vue-i18n"

/**
 * Composable for handling gender-based translations
 */
export function useGenderTranslations(props: {
  user: ReturnType<typeof useUser>
  i18n: Composer
  i18nUtilities: {
    tmsafe: (key: string) => string | string[] | Record<string, any> | null
    tsafe: (key: string, params: Record<string, any>) => string | string[] | Record<string, any>
  }
}) {
  const { user, i18n, i18nUtilities } = props

  /**
   * Resolves translation for a given key, supporting both array and regular translations.
   * @param key - The translation key to resolve.
   * @param params - Parameters to pass to the translation function.
   * @returns The resolved translation or null if not found.
   */
  function resolveTranslation<T>(key: string, params: Record<string, any> = {}): T | null {
    const resObject = i18nUtilities.tmsafe(key)

    if (resObject && typeof resObject === "object") {
      // If some of the array objects has a male/female property, recursively resolve the translation for the gender property
      if (Array.isArray(resObject) && resObject.some((item: any) => item.male || item.female)) {
        return resObject.map((item: any) => {
          if (item.male || item.female) return item[user.genderPronoun?.value]
          return item
        }) as T
      }

      if (!isEmpty(resObject)) {
        return resObject as T
      }
    }

    // Check if translation exists
    if (i18n.te(key)) {
      return i18nUtilities.tsafe(key, params) as T
    }

    return null
  }

  /**
   * Implements a fallback strategy for gender-specific translations by attempting alternative genders and formats.
   * @param key - The base translation key without a gender suffix.
   * @param params - Parameters to pass to the translation function.
   * @param effectiveGender - The primary gender that was already attempted and failed.
   * @returns The translated string using fallback mechanisms, or an empty string as a last resort.
   */
  function resolveGenderedFallbacks<T>(key: string, params: Record<string, any>, effectiveGender: string): T {
    // Creates an array of fallback genders by excluding the effectiveGender from the list of all possible genders.
    const fallbacks = ["male", "female"].filter((g) => g !== effectiveGender)

    // Try each fallback gender
    for (const fallback of fallbacks) {
      const fallbackKey = `${key}.${fallback}`
      const result = resolveTranslation<T>(fallbackKey, params)

      if (result !== null) {
        return result
      }
    }

    // Try the base key as a last resort
    return resolveTranslation<T>(key, params) ?? ("" as T)
  }

  /**
   * Provides gender-based translations, localizing labels by both the selected language and the user's gender.
   * @param key - The translation key.
   * @param params - Parameters to pass to the translation function.
   * @param forceGender - Optional parameter to force a specific gender ("male", "female").
   * @returns The gender-specific translation or falls back to other genders if the specific one doesn't exist.
   */
  function tg<T = string | string[]>(
    key: string,
    params: Record<string, any> = {},
    forceGender?: "male" | "female"
  ): T {
    const gender = forceGender || user.genderPronoun?.value
    const effectiveGender = i18n.locale.value === "en" && gender !== "female" ? "male" : gender

    // Try direct gender translation
    const genderKey = `${key}.${effectiveGender}`
    const translatedGender = resolveTranslation<T>(genderKey, params)

    // if translatedGender is null return the fallback to other genders if the specific one doesn't exist
    return translatedGender ?? resolveGenderedFallbacks<T>(key, params, effectiveGender)
  }

  return { tg }
}
